/**
 * Window Manager Context
 *
 * Manages window state including positions, sizes, z-index ordering,
 * and focus states for the Mac Studio theme's draggable window system.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'

const STORAGE_KEY = 'mac-studio-windows'
const DEFAULT_WINDOW_WIDTH = 400
const DEFAULT_WINDOW_HEIGHT = 300
const MIN_WINDOW_WIDTH = 200
const MIN_WINDOW_HEIGHT = 150

// Default positions and sizes for each window (used by Restore Defaults)
const DEFAULT_WINDOW_CONFIGS: Record<string, { position: WindowPosition; size: WindowSize }> = {
  'portfolio-window': { position: { x: 20, y: 20 }, size: { width: 480, height: 400 } },
  'hero-window': { position: { x: 240, y: 20 }, size: { width: 800, height: 700 } },
  'gated-content-window': { position: { x: 250, y: 450 }, size: { width: 480, height: 400 } },
  'stats-window': { position: { x: 20, y: 380 }, size: { width: 800, height: 700 } },
  'tech-window': { position: { x: 20, y: 520 }, size: { width: 480, height: 400 } },
}

// Cascade offset between windows
const CASCADE_OFFSET = 40
const CASCADE_START = { x: 60, y: 60 }

export interface WindowPosition {
  x: number
  y: number
}

export interface WindowSize {
  width: number
  height: number
}

export interface WindowState {
  id: string
  position: WindowPosition
  size: WindowSize
  zIndex: number
  isMinimized: boolean
  isMaximized: boolean
}

interface WindowManagerContextValue {
  /** All window states */
  windows: Record<string, WindowState>
  /** Currently focused window ID */
  focusedWindowId: string | null
  /** Register a new window */
  registerWindow: (id: string, initialPosition?: WindowPosition, initialSize?: WindowSize) => void
  /** Unregister a window */
  unregisterWindow: (id: string) => void
  /** Bring window to front and focus it */
  focusWindow: (id: string) => void
  /** Update window position */
  updatePosition: (id: string, position: WindowPosition) => void
  /** Update window size */
  updateSize: (id: string, size: WindowSize) => void
  /** Minimize window */
  minimizeWindow: (id: string) => void
  /** Maximize window */
  maximizeWindow: (id: string) => void
  /** Restore window from minimized/maximized state */
  restoreWindow: (id: string) => void
  /** Reset all windows to default positions (legacy - use cascadeWindows) */
  resetAllPositions: () => void
  /** Get window state by ID */
  getWindowState: (id: string) => WindowState | undefined
  /** Whether window management is enabled (desktop only) */
  isEnabled: boolean
  /** Minimum window dimensions */
  minDimensions: { width: number; height: number }
  // Window arrangement functions
  /** Cascade windows diagonally */
  cascadeWindows: () => void
  /** Tile all windows in a grid */
  tileAllWindows: () => void
  /** Minimize all windows (Show Desktop) */
  showDesktop: () => void
  /** Restore windows to their original default positions */
  restoreDefaults: () => void
  /** Center all windows on screen (stacked) */
  centerAllWindows: () => void
  /** Smart fit - arrange windows to show all content without overlapping */
  smartFit: () => void
}

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null)

export function useWindowManager() {
  const context = useContext(WindowManagerContext)
  if (!context) {
    throw new Error('useWindowManager must be used within a WindowManagerProvider')
  }
  return context
}

// Safe hook that returns null if context is not available
export function useWindowManagerSafe() {
  return useContext(WindowManagerContext)
}

interface WindowManagerProviderProps {
  children: ReactNode
  /** Disable window management (for mobile) */
  disabled?: boolean
}

export function WindowManagerProvider({
  children,
  disabled = false,
}: WindowManagerProviderProps) {
  const [windows, setWindows] = useState<Record<string, WindowState>>({})
  const [focusedWindowId, setFocusedWindowId] = useState<string | null>(null)
  const [nextZIndex, setNextZIndex] = useState(1)

  // Load saved positions from localStorage
  useEffect(() => {
    if (typeof window === 'undefined' || disabled) return

    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setWindows(parsed)
        // Find highest z-index
        const maxZ = Object.values(parsed as Record<string, WindowState>)
          .reduce((max, w) => Math.max(max, w.zIndex), 0)
        setNextZIndex(maxZ + 1)
      } catch {
        // Invalid stored data, start fresh
      }
    }
  }, [disabled])

  // Save positions to localStorage (debounced)
  useEffect(() => {
    if (typeof window === 'undefined' || disabled) return

    const timeout = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(windows))
    }, 300)

    return () => clearTimeout(timeout)
  }, [windows, disabled])

  // Calculate default position for new windows (cascade effect)
  const getDefaultPosition = useCallback((): WindowPosition => {
    const windowCount = Object.keys(windows).length
    const offset = 30 * windowCount
    return {
      x: 100 + offset,
      y: 80 + offset,
    }
  }, [windows])

  // Register a new window
  const registerWindow = useCallback(
    (id: string, initialPosition?: WindowPosition, initialSize?: WindowSize) => {
      setWindows((prev) => {
        // If window already exists, don't re-register
        if (prev[id]) return prev

        const position = initialPosition ?? getDefaultPosition()
        const size = initialSize ?? { width: DEFAULT_WINDOW_WIDTH, height: DEFAULT_WINDOW_HEIGHT }

        return {
          ...prev,
          [id]: {
            id,
            position,
            size,
            zIndex: nextZIndex,
            isMinimized: false,
            isMaximized: false,
          },
        }
      })
      setNextZIndex((z) => z + 1)
    },
    [getDefaultPosition, nextZIndex]
  )

  // Unregister a window
  const unregisterWindow = useCallback((id: string) => {
    setWindows((prev) => {
      const { [id]: _, ...rest } = prev
      return rest
    })
    setFocusedWindowId((prev) => (prev === id ? null : prev))
  }, [])

  // Focus a window (bring to front)
  const focusWindow = useCallback((id: string) => {
    setFocusedWindowId(id)
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          zIndex: nextZIndex,
        },
      }
    })
    setNextZIndex((z) => z + 1)
  }, [nextZIndex])

  // Update window position
  const updatePosition = useCallback((id: string, position: WindowPosition) => {
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          position,
        },
      }
    })
  }, [])

  // Update window size
  const updateSize = useCallback((id: string, size: WindowSize) => {
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          size: {
            width: Math.max(size.width, MIN_WINDOW_WIDTH),
            height: Math.max(size.height, MIN_WINDOW_HEIGHT),
          },
        },
      }
    })
  }, [])

  // Minimize window
  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          isMinimized: true,
          isMaximized: false,
        },
      }
    })
  }, [])

  // Maximize window
  const maximizeWindow = useCallback((id: string) => {
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          isMaximized: true,
          isMinimized: false,
        },
      }
    })
  }, [])

  // Restore window
  const restoreWindow = useCallback((id: string) => {
    setWindows((prev) => {
      if (!prev[id]) return prev
      return {
        ...prev,
        [id]: {
          ...prev[id],
          isMinimized: false,
          isMaximized: false,
        },
      }
    })
  }, [])

  // Reset all positions (auto-arrange) - legacy, calls cascadeWindows
  const resetAllPositions = useCallback(() => {
    setWindows((prev) => {
      const entries = Object.entries(prev)
      const newWindows: Record<string, WindowState> = {}

      entries.forEach(([id, state], index) => {
        newWindows[id] = {
          ...state,
          position: {
            x: CASCADE_START.x + (index * CASCADE_OFFSET),
            y: CASCADE_START.y + (index * CASCADE_OFFSET),
          },
          isMinimized: false,
          isMaximized: false,
        }
      })

      return newWindows
    })
  }, [])

  // Cascade windows diagonally with proper offsets
  const cascadeWindows = useCallback(() => {
    setWindows((prev) => {
      const entries = Object.entries(prev)
      const newWindows: Record<string, WindowState> = {}

      entries.forEach(([id, state], index) => {
        newWindows[id] = {
          ...state,
          position: {
            x: CASCADE_START.x + (index * CASCADE_OFFSET),
            y: CASCADE_START.y + (index * CASCADE_OFFSET),
          },
          isMinimized: false,
          isMaximized: false,
        }
      })

      return newWindows
    })
  }, [])

  // Tile all windows in a grid layout
  const tileAllWindows = useCallback(() => {
    setWindows((prev) => {
      const entries = Object.entries(prev).filter(([_, state]) => !state.isMinimized || true)
      const windowCount = entries.length
      if (windowCount === 0) return prev

      // Get viewport dimensions (with margins)
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth - 40 : 1200
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight - 80 : 800 // Account for menu bar
      const menuBarHeight = 28
      const margin = 10
      const startY = menuBarHeight + margin

      // Calculate grid dimensions
      const cols = windowCount <= 2 ? windowCount : windowCount <= 4 ? 2 : 3
      const rows = Math.ceil(windowCount / cols)

      // Calculate tile size
      const tileWidth = Math.floor((viewportWidth - (margin * (cols + 1))) / cols)
      const tileHeight = Math.floor((viewportHeight - startY - (margin * rows)) / rows)

      const newWindows: Record<string, WindowState> = {}

      entries.forEach(([id, state], index) => {
        const col = index % cols
        const row = Math.floor(index / cols)

        newWindows[id] = {
          ...state,
          position: {
            x: margin + (col * (tileWidth + margin)),
            y: startY + (row * (tileHeight + margin)),
          },
          size: {
            width: Math.max(tileWidth, MIN_WINDOW_WIDTH),
            height: Math.max(tileHeight, MIN_WINDOW_HEIGHT),
          },
          isMinimized: false,
          isMaximized: false,
        }
      })

      return newWindows
    })
  }, [])

  // Show Desktop - minimize all windows
  const showDesktop = useCallback(() => {
    setWindows((prev) => {
      const newWindows: Record<string, WindowState> = {}

      Object.entries(prev).forEach(([id, state]) => {
        newWindows[id] = {
          ...state,
          isMinimized: true,
          isMaximized: false,
        }
      })

      return newWindows
    })
    setFocusedWindowId(null)
  }, [])

  // Restore windows to their original default positions and sizes
  const restoreDefaults = useCallback(() => {
    setWindows((prev) => {
      const newWindows: Record<string, WindowState> = {}

      Object.entries(prev).forEach(([id, state]) => {
        const defaultConfig = DEFAULT_WINDOW_CONFIGS[id]

        if (defaultConfig) {
          newWindows[id] = {
            ...state,
            position: defaultConfig.position,
            size: defaultConfig.size,
            isMinimized: false,
            isMaximized: false,
          }
        } else {
          // Fallback for unknown windows
          newWindows[id] = {
            ...state,
            position: { x: 100, y: 80 },
            isMinimized: false,
            isMaximized: false,
          }
        }
      })

      return newWindows
    })
  }, [])

  // Center all windows on screen (stacked)
  const centerAllWindows = useCallback(() => {
    setWindows((prev) => {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800
      const menuBarHeight = 28

      const newWindows: Record<string, WindowState> = {}

      Object.entries(prev).forEach(([id, state]) => {
        const centerX = Math.max(0, (viewportWidth - state.size.width) / 2)
        const centerY = Math.max(menuBarHeight, (viewportHeight - state.size.height) / 2)

        newWindows[id] = {
          ...state,
          position: {
            x: centerX,
            y: centerY,
          },
          isMinimized: false,
          isMaximized: false,
        }
      })

      return newWindows
    })
  }, [])

  // Smart Fit - arrange windows to show all content without overlapping
  const smartFit = useCallback(() => {
    setWindows((prev) => {
      const entries = Object.entries(prev)
      const windowCount = entries.length
      if (windowCount === 0) return prev

      // Get viewport dimensions
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800
      const menuBarHeight = 28
      const margin = 15 // Spacing between windows
      const startY = menuBarHeight + margin

      // Get optimal sizes for each window from defaults, scaled if needed
      const windowConfigs = entries.map(([id, state]) => {
        const defaultConfig = DEFAULT_WINDOW_CONFIGS[id]
        const optimalSize = defaultConfig?.size || state.size
        return {
          id,
          state,
          optimalWidth: optimalSize.width,
          optimalHeight: optimalSize.height,
        }
      })

      // Calculate total width needed and available space
      const availableWidth = viewportWidth - (margin * 2)
      const availableHeight = viewportHeight - startY - margin

      // Try to fit windows in rows, keeping their optimal sizes
      const newWindows: Record<string, WindowState> = {}
      let currentX = margin
      let currentY = startY
      let rowHeight = 0

      windowConfigs.forEach(({ id, state, optimalWidth, optimalHeight }) => {
        // Scale down if window is too large for viewport
        let finalWidth = Math.min(optimalWidth, availableWidth - margin)
        let finalHeight = Math.min(optimalHeight, availableHeight - margin)

        // Check if window fits in current row
        if (currentX + finalWidth + margin > viewportWidth) {
          // Move to next row
          currentX = margin
          currentY += rowHeight + margin
          rowHeight = 0
        }

        // Check if window fits vertically
        if (currentY + finalHeight > viewportHeight - margin) {
          // Scale down to fit
          const scale = (viewportHeight - margin - currentY) / finalHeight
          if (scale > 0.5) {
            finalHeight = Math.floor(finalHeight * scale)
            finalWidth = Math.floor(finalWidth * scale)
          }
        }

        newWindows[id] = {
          ...state,
          position: {
            x: currentX,
            y: currentY,
          },
          size: {
            width: Math.max(finalWidth, MIN_WINDOW_WIDTH),
            height: Math.max(finalHeight, MIN_WINDOW_HEIGHT),
          },
          isMinimized: false,
          isMaximized: false,
        }

        currentX += finalWidth + margin
        rowHeight = Math.max(rowHeight, finalHeight)
      })

      return newWindows
    })
  }, [])

  // Get window state
  const getWindowState = useCallback(
    (id: string) => windows[id],
    [windows]
  )

  const value: WindowManagerContextValue = {
    windows,
    focusedWindowId,
    registerWindow,
    unregisterWindow,
    focusWindow,
    updatePosition,
    updateSize,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    resetAllPositions,
    getWindowState,
    isEnabled: !disabled,
    minDimensions: { width: MIN_WINDOW_WIDTH, height: MIN_WINDOW_HEIGHT },
    // Window arrangement functions
    cascadeWindows,
    tileAllWindows,
    showDesktop,
    restoreDefaults,
    centerAllWindows,
    smartFit,
  }

  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  )
}
